import { currencySchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { organizationBrandingContract } from './common/organization-branding.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';
import { institutionSchema, pseudonymSchema } from './org-unit.js';
import {
  organizationAccountFiguresSchema,
  organizationPlacementSchema,
} from './organization-figures.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const legalTypeSchema = z.enum(['association', 'foundation', 'company']);
export type LegalType = z.infer<typeof legalTypeSchema>;

export const accountKindSchema = z.enum(['bank', 'paypal', 'pos', 'cash', 'stripe']);
export type AccountKind = z.infer<typeof accountKindSchema>;

export const organizationAccountSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  kind: accountKindSchema,
  label: z.string(),
  /** The electronic form — upper case, no spaces; the screen groups it for reading. */
  iban: z.string().nullable(),
  currency: currencySchema,
  isActive: z.boolean(),
});

export type OrganizationAccount = z.infer<typeof organizationAccountSchema>;

/**
 * One public donation host. The host decides which organization a visitor is
 * on, and therefore which organization receives a stranger's money, so a site
 * is added, retired and re-pointed behind `organization.edit` at global scope
 * alone — docs/decisions/0036 *Amended 2026-09-17*.
 */
export const organizationSiteSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  /** Lower-case, no scheme, no port and no path — the form it is compared in. */
  host: z.string(),
  isCanonical: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
});

export type OrganizationSite = z.infer<typeof organizationSiteSchema>;

/**
 * `hasHeadquarters` is the fact the sites screen states rather than discovers
 * later: an organization with no headquarters institution can have its site
 * take a payment and **cannot turn it into a donation**, so the screen that
 * opens a site is where that is said — docs/decisions/0054 *Amended
 * 2026-09-18*.
 */
export const organizationSitesSchema = z.object({
  items: z.array(organizationSiteSchema),
  hasHeadquarters: z.boolean(),
  writable: z.boolean(),
});

export type OrganizationSites = z.infer<typeof organizationSitesSchema>;

export const organizationSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  pseudonym: z.string().nullable(),
  code: z.string(),
  taxNumber: z.string().nullable(),
  legalType: legalTypeSchema,
  baseCurrency: currencySchema,
  isActive: z.boolean(),
  institutionCount: z.number().int(),
  accountCount: z.number().int(),
  /** Null for an organization nobody has placed: the screen says *Konum atanmamış*. */
  placement: organizationPlacementSchema.nullable(),
  createdAt: z.string(),
});

export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

export const organizationSchema = organizationSummarySchema.extend({
  adminHost: z.string().nullable(),
  accounts: z.array(organizationAccountSchema),
});

export type Organization = z.infer<typeof organizationSchema>;

export const ORGANIZATION_SORT_FIELDS = ['name', 'code', 'createdAt'] as const;
export type OrganizationSortField = (typeof ORGANIZATION_SORT_FIELDS)[number];

export const organizationStatusFilterSchema = z.enum(['active', 'inactive']);

/**
 * An account's IBAN arrives however a person typed it. It is not shaped here:
 * the server reads it into its electronic form and answers a named 422 — a
 * checksum, a length, a country — which a refusal at the boundary could not.
 */
const ibanInputSchema = z.string().trim().max(64).nullable();

/**
 * Legal entities and the accounts money arrives in. **Reads** are organization
 * scope, the rule reconciliation already uses: a reader below global scope sees
 * an organization only when every institution linked to it lies inside that
 * scope — docs/decisions/0045-reconciliation-is-scoped-by-the-whole-organization.md.
 * **Writes** are global scope alone, and every read the screen makes says so in
 * `writable`, so the screen offers a write the server would not refuse.
 */
export const organizationContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/organizations',
      summary: 'Legal entities holding bank accounts, those the caller’s scope wholly holds',
    })
    .errors(guarded)
    .input(
      sortedListRequest(ORGANIZATION_SORT_FIELDS, 'name', 'asc').extend({
        status: organizationStatusFilterSchema.optional(),
        legalType: legalTypeSchema.optional(),
        regionId: z.uuid().optional(),
        zoneId: z.uuid().optional(),
        countryId: z.uuid().optional(),
      }),
    )
    .output(paginated(organizationSummarySchema).extend({ writable: z.boolean() })),

  get: oc
    .route({ method: 'GET', path: '/organizations/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        organization: organizationSchema,
        writable: z.boolean(),
        /**
         * Whether the reader's scope wholly holds the organization under
         * docs/decisions/0045 — what the credential, sender and site reads ask.
         * False when the organization is read only because it is placed inside
         * the reader's scope (docs/decisions/0127 §3).
         */
        organizationInScope: z.boolean(),
      }),
    ),

  accountFigures: oc
    .route({
      method: 'GET',
      path: '/organizations/{id}/account-figures',
      summary:
        'What each account holds: balance, reconciled, pending and open, and the reconciled money by institution',
    })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(organizationAccountFiguresSchema),

  institutions: oc
    .route({
      method: 'GET',
      path: '/organizations/{id}/institutions',
      summary: 'The institutions whose collections land in the organization',
    })
    .errors(guarded)
    .input(paginationInputSchema.extend({ id: z.uuid() }))
    .output(paginated(institutionSchema)),

  create: oc
    .route({ method: 'POST', path: '/organizations' })
    .errors(guarded)
    .input(
      z.object({
        name: z.string().trim().min(2).max(200),
        code: z
          .string()
          .trim()
          .min(2)
          .max(50)
          .regex(/^[A-Z0-9-]+$/, 'Uppercase letters, digits and - only'),
        taxNumber: z.string().trim().max(50).nullable().default(null),
        legalType: legalTypeSchema.default('association'),
        baseCurrency: currencySchema.default('EUR'),
        countryId: z.uuid().nullable().default(null),
      }),
    )
    .output(z.object({ organization: organizationSchema })),

  update: oc
    .route({ method: 'PATCH', path: '/organizations/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().trim().min(2).max(200).optional(),
        pseudonym: pseudonymSchema.nullable().optional(),
        taxNumber: z.string().trim().max(50).nullable().optional(),
        legalType: legalTypeSchema.optional(),
        baseCurrency: currencySchema.optional(),
        adminHost: z.string().trim().min(3).max(253).nullable().optional(),
        countryId: z.uuid().nullable().optional(),
        isActive: z.boolean().optional(),
      }),
    )
    .output(z.object({ organization: organizationSchema })),

  addAccount: oc
    .route({ method: 'POST', path: '/organizations/{id}/accounts' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        kind: accountKindSchema,
        label: z.string().trim().min(2).max(200),
        iban: ibanInputSchema.default(null),
        currency: currencySchema,
      }),
    )
    .output(z.object({ account: organizationAccountSchema })),

  /**
   * An account's kind and currency are what its lines were written in, so they
   * are fixed once it exists; a wrong one is deactivated and added again.
   */
  updateAccount: oc
    .route({ method: 'PATCH', path: '/organizations/{id}/accounts/{accountId}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        accountId: z.uuid(),
        label: z.string().trim().min(2).max(200).optional(),
        iban: ibanInputSchema.optional(),
        isActive: z.boolean().optional(),
      }),
    )
    .output(z.object({ account: organizationAccountSchema })),

  logo: {
    get: oc
      .route({ method: 'GET', path: '/organizations/{id}/logo' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(
        z.object({
          logoUrl: z.string().nullable(),
          scanStatus: z.enum(['pending', 'clean', 'infected', 'failed']).nullable(),
        }),
      ),
    requestUpload: oc
      .route({ method: 'POST', path: '/organizations/{id}/logo/upload' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          fileName: fileNameSchema,
          fileSize: z
            .number()
            .int()
            .min(1)
            .max(5 * 1024 * 1024),
        }),
      )
      .output(presignedUploadSchema),
    set: oc
      .route({ method: 'PUT', path: '/organizations/{id}/logo' })
      .errors(guarded)
      .input(z.object({ id: z.uuid(), uploadToken: uploadTokenSchema }))
      .output(z.object({ saved: z.literal(true) })),
    remove: oc
      .route({ method: 'DELETE', path: '/organizations/{id}/logo' })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(z.object({ removed: z.literal(true) })),
  },
  branding: organizationBrandingContract,
  sites: {
    list: oc
      .route({
        method: 'GET',
        path: '/organizations/{id}/sites',
        summary: 'The hosts the organization\u2019s public donation site answers on',
      })
      .errors(guarded)
      .input(z.object({ id: z.uuid() }))
      .output(organizationSitesSchema),

    add: oc
      .route({ method: 'POST', path: '/organizations/{id}/sites' })
      .errors(guarded)
      .input(
        z.object({
          id: z.uuid(),
          host: z.string().trim().min(3).max(253),
          isCanonical: z.boolean().default(false),
        }),
      )
      .output(z.object({ site: organizationSiteSchema })),

    /**
     * A host is never edited into another host: re-pointing a name is retiring
     * one site and adding another, so an order already placed keeps saying
     * which organization took it.
     */
    update: oc
      .route({ method: 'PATCH', path: '/organizations/sites/{siteId}' })
      .errors(guarded)
      .input(
        z.object({
          siteId: z.uuid(),
          isCanonical: z.boolean().optional(),
          isActive: z.boolean().optional(),
        }),
      )
      .output(z.object({ site: organizationSiteSchema })),

    remove: oc
      .route({ method: 'DELETE', path: '/organizations/sites/{siteId}' })
      .errors(guarded)
      .input(z.object({ siteId: z.uuid() }))
      .output(z.object({ removed: z.literal(true) })),
  },
};
